import React, { useEffect, useState, useRef, useMemo } from "react";
import { getAllHybridPackages, addUserToMatrix } from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import CustomTable from "../../components/custom-table/CustomTable";
import { toast } from 'react-toastify';
import "./style.css";

function AllHybridPackages() {
  const [isLoading, setIsLoading] = useState(true);
  const [rawPackages, setRawPackages] = useState([]);
  const [totalPackages, setTotalPackages] = useState(0);
  const [todayPackages, setTodayPackages] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [addingUserId, setAddingUserId] = useState(null);
  const addingLockRef = useRef(new Set());

  const columns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "name" },
    { Header: "Level", accessor: "level" },
    { Header: "Position", accessor: "position" },
    { Header: "Status", accessor: "status" },
    { Header: "Txn ID", accessor: "txnId" },
    { Header: "Created At", accessor: "createdAt" },
    { Header: "HM", accessor: "matrix" },
  ];

  const handleAddToMatrix = async (userId) => {
    if (addingLockRef.current.has(userId)) return;
    addingLockRef.current.add(userId);

    try {
      setAddingUserId(userId);
      const result = await addUserToMatrix(userId);
      if (result.success) {
        toast.success(`User ${userId} added to HM Part 1 successfully`);
        fetchAllHybridPackages();
      } else {
        toast.error(result.message || "Failed to add user to HM");
      }
    } catch (error) {
      console.error("Error adding user to HM:", error);
      toast.error(error.message || "Failed to add user to HM");
    } finally {
      addingLockRef.current.delete(userId);
      setAddingUserId(null);
    }
  };

  // Derive table data on every render so button reacts to addingUserId changes
  const hybridPackageList = useMemo(() => {
    return rawPackages.map((pkg, index) => ({
      sno: index + 1,
      userId: pkg.userId || "N/A",
      name: pkg.name || "N/A",
      level: pkg.position || "-",
      position: pkg.position || "-",
      status: (
        <span
          style={{
            color:
              pkg.status === "Active"
                ? "green"
                : pkg.status === "Inactive"
                ? "red"
                : pkg.status === "Mature"
                ? "orange"
                : "gray",
          }}
        >
          {pkg.status}
        </span>
      ),
      txnId: pkg.txnId ? (
        <a
          href={`https://bscscan.com/tx/${pkg.txnId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          BSC Scan
        </a>
      ) : (
        "-"
      ),
      createdAt: pkg.createdAt ? formatDate(pkg.createdAt) : "N/A",
      matrix: pkg.inMatrix ? (
        <span style={{ color: "green", fontWeight: "bold" }}>In HM</span>
      ) : (
        <button
          className="add-to-matrix-btn"
          onClick={() => handleAddToMatrix(pkg.userId)}
          disabled={addingUserId === pkg.userId}
          style={{
            opacity: addingUserId === pkg.userId ? 0.7 : 1,
            cursor: addingUserId === pkg.userId ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          {addingUserId === pkg.userId && (
            <span
              className="btn-spinner"
              style={{
                width: "14px",
                height: "14px",
                border: "2px solid rgba(255,255,255,0.3)",
                borderTop: "2px solid #fff",
                borderRadius: "50%",
                display: "inline-block",
                animation: "spin 0.6s linear infinite",
              }}
            />
          )}
          {addingUserId === pkg.userId ? "Adding..." : "Add to HM"}
        </button>
      ),
    }));
  }, [rawPackages, addingUserId]);

  const fetchAllHybridPackages = async () => {
    try {
      const hybridPackagesData = await getAllHybridPackages();

      if (!hybridPackagesData || !hybridPackagesData.data) {
        setRawPackages([]);
        setTotalPackages(0);
        setTodayPackages(0);
        setTotalAmount(0);
        setIsLoading(false);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayCount = 0;
      let totalAmountValue = 0;

      hybridPackagesData.data.forEach(pkg => {
        totalAmountValue += 10;
        const createdDate = new Date(pkg.createdAt);
        createdDate.setHours(0, 0, 0, 0);
        if (createdDate.getTime() === today.getTime()) {
          todayCount++;
        }
      });

      setRawPackages(hybridPackagesData.data);
      setTotalPackages(hybridPackagesData.data.length);
      setTodayPackages(todayCount);
      setTotalAmount(totalAmountValue);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching hybrid packages:", error);
      toast.error("Failed to load hybrid packages");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllHybridPackages();
  }, []);

  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="investment-history">
          <h1 className="main-heading">All Hybrid Packages</h1>
          <br />

          <div className="stats-container">
            <div className="stat-card">
              <div className="stat-left">
                <p className="stat-label">Total Packages</p>
                <h2 className="stat-value">{totalPackages}</h2>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-left">
                <p className="stat-label">Today Packages</p>
                <h2 className="stat-value">{todayPackages}</h2>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-left">
                <p className="stat-label">Total Amount</p>
                <h2 className="stat-value">${totalAmount.toFixed(2)}</h2>
              </div>
            </div>
          </div>

          <br />
          <CustomTable
            data={hybridPackageList}
            columns={columns}
            heading={`All Hybrid Packages (${hybridPackageList.length})`}
          />
        </div>
      )}
    </>
  );
}

export default AllHybridPackages;
